import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Zap, TrendingUp, Sparkles, Percent, Ticket, ShoppingCart } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import AppButton from '@/components/ui/AppButton';

const ImpulsoCompra = ({ sorteos }) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  // Filtrar sorteos que tienen paquetes activos
  const sorteosConPaquetes = sorteos.filter(s => 
    (s.estado === 'published' || s.estado === 'activo') && 
    s.paquetes && s.paquetes.length > 0
  );

  if (sorteosConPaquetes.length === 0) return null;

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  const handleComprarPaquete = (sorteo, paquete) => {
    navigate(`/sorteo/${sorteo.landing_slug}?paquete=${paquete.id}`);
  };

  return (
    <div className="space-y-6 my-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex items-center justify-between px-1">
        <div>
          <h2 className="text-2xl font-black uppercase tracking-tighter flex items-center gap-2 text-foreground">
            <Sparkles className="w-6 h-6 text-yellow-500 fill-yellow-500" />
            🎟️ Aumenta tus oportunidades de ganar
          </h2>
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest">
            Compra más boletos y mejora tus probabilidades
          </p>
        </div>
        <div className="hidden md:flex gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            className="rounded-full w-10 h-10 border-white/20 bg-white/5 backdrop-blur-md"
            onClick={() => scroll('left')}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            className="rounded-full w-10 h-10 border-white/20 bg-white/5 backdrop-blur-md"
            onClick={() => scroll('right')}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div 
        ref={scrollRef}
        className="flex overflow-x-auto gap-6 pb-6 px-1 no-scrollbar snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {sorteosConPaquetes.map((sorteo) => 
          sorteo.paquetes.filter(p => p.activo).map((paquete, idx) => {
            const precioNormal = sorteo.precio_boleto * paquete.cantidad;
            const tieneDescuento = paquete.descuento > 0;
            // El descuento en DB viene como entero (ej: 10 para 10%)
            const precioFinal = precioNormal * (1 - (paquete.descuento / 100));
            
            // Determinar si es el "más popular" (ej: el de en medio o con más descuento)
            const esMasPopular = paquete.etiqueta?.toLowerCase().includes('popular') || (!paquete.etiqueta && idx === 1);

            return (
              <div 
                key={`${sorteo.id}-${paquete.id}`}
                className="min-w-[280px] md:min-w-[320px] snap-start"
              >
                <Card 
                  className={`relative h-full overflow-hidden transition-all duration-300 hover:scale-[1.03] group ${
                    esMasPopular 
                    ? 'border-purple-500/50 shadow-[0_0_30px_rgba(168,85,247,0.15)] bg-gradient-to-br from-white/10 to-purple-500/5' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                  } backdrop-blur-xl border-2`}
                >
                  {/* Glow Effect */}
                  <div className={`absolute -top-24 -right-24 w-48 h-48 rounded-full blur-[80px] opacity-20 pointer-events-none transition-all duration-500 group-hover:scale-150 ${
                    esMasPopular ? 'bg-purple-500' : 'bg-blue-500'
                  }`} />

                  <CardContent className="p-6 flex flex-col h-full relative z-10">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex flex-col">
                        {paquete.etiqueta && (
                          <Badge className="mb-2 bg-gradient-to-r from-purple-600 to-blue-600 border-none font-black uppercase text-[10px] tracking-widest animate-pulse">
                            {paquete.etiqueta}
                          </Badge>
                        )}
                        {esMasPopular && !paquete.etiqueta && (
                          <Badge className="mb-2 bg-gradient-to-r from-orange-500 to-red-600 border-none font-black uppercase text-[10px] tracking-widest">
                            🔥 MÁS COMPRADO
                          </Badge>
                        )}
                        <span className="text-4xl font-black tracking-tighter text-foreground flex items-baseline gap-1">
                          {paquete.cantidad}
                          <span className="text-sm font-bold text-muted-foreground uppercase">Boletos</span>
                        </span>
                      </div>
                      <div className="p-3 rounded-2xl bg-white/10 border border-white/10 text-primary">
                        <Zap className={`w-6 h-6 ${esMasPopular ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-sm font-black uppercase tracking-widest text-muted-foreground truncate mb-1">
                        Sorteo: {sorteo.titulo}
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-black text-foreground">
                          {formatCurrency(precioFinal)}
                        </span>
                        {tieneDescuento && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="line-through text-muted-foreground font-bold">
                              {formatCurrency(precioNormal)}
                            </span>
                            <span className="bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full font-black">
                              -{paquete.descuento}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-auto space-y-3">
                      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-70">
                        <TrendingUp className="w-3 h-3" />
                        Aumenta tus probabilidades
                      </div>
                      
                      <AppButton 
                        variant="primary"
                        onClick={() => handleComprarPaquete(sorteo, paquete)}
                        icon={ShoppingCart}
                        className="w-full"
                      >
                        Comprar Paquete
                      </AppButton>
                    </div>
                  </CardContent>

                  {/* Aesthetic Corner Ticket Cutout */}
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-slate-950/20 border-2 border-white/10" />
                </Card>
              </div>
            );
          })
        )}
      </div>

      <style jsx>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default ImpulsoCompra;
